import { useEffect, useRef } from "react";
import first from "lodash/first";
import last from "lodash/last";

import { useLocationRuleNotes } from "globals/hooks/useLocationRuleNotes";
import { useCurrentUser } from "globals/hooks";
import { CreateStopState } from "../context/initialState";

type UseLocationAutoFillNotesParams = {
  stops: CreateStopState[];
  dateTime?: string;
  currentNote: string;
  onNoteChange: (note: string) => void;
};

const appendNote = (
  existing: string | null | undefined,
  addition: string | null | undefined
): string | undefined => {
  if (!addition) return undefined;
  if (existing) return `${existing}\n${addition}`;
  return addition;
};

const replaceAutoFilledNote = (
  currentNote: string | null | undefined,
  previousAutoFill: string | null | undefined,
  newAutoFill: string | null | undefined
): string | undefined => {
  if (!previousAutoFill && !newAutoFill) return undefined;

  const current = currentNote || "";

  let cleaned = current;
  if (previousAutoFill && current.includes(previousAutoFill)) {
    cleaned = current.replace(previousAutoFill, "");
    cleaned = cleaned.replace(/\n{2,}/g, "\n").replace(/^\n|\n$/g, "").trim();
  }

  if (!newAutoFill) return cleaned || undefined;
  if (cleaned) return `${cleaned}\n${newAutoFill}`;
  return newAutoFill;
};

export const useLocationAutoFillNotes = ({
  stops,
  dateTime,
  currentNote,
  onNoteChange,
}: UseLocationAutoFillNotesParams) => {
  const { evaluateLocationNotes } = useLocationRuleNotes();
  const currentUser = useCurrentUser();
  const autoFillRef = useRef("");
  const contactNoteAppliedRef = useRef(false);

  // Auto-fill contact default trip note
  useEffect(() => {
    if (contactNoteAppliedRef.current) return;

    const contactDefaultNote = currentUser?.defaultTripNote;
    if (!contactDefaultNote) return;

    contactNoteAppliedRef.current = true;

    const newNote = appendNote(currentNote, contactDefaultNote);
    if (newNote !== undefined) {
      onNoteChange(newNote);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.defaultTripNote]);

  // Auto-fill location rule notes
  useEffect(() => {
    const updateLocationNotes = async () => {
      const pickupStop = first(stops);
      const dropoffStop = last(stops);
      const pickupAddress = pickupStop?.location || "";
      const dropoffAddress = dropoffStop?.location || "";
      const pickupDateTime = dateTime || pickupStop?.dateTime || undefined;

      if (!pickupAddress || !dropoffAddress) return;

      const locationNotes = await evaluateLocationNotes(
        pickupAddress,
        dropoffAddress,
        pickupDateTime
      );

      if (!locationNotes) return;

      const newNote = replaceAutoFilledNote(
        currentNote,
        autoFillRef.current,
        locationNotes.tripNote
      );

      autoFillRef.current = locationNotes.tripNote || "";

      if (newNote !== undefined) {
        onNoteChange(newNote);
      }
    };

    updateLocationNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stops, dateTime, evaluateLocationNotes]);
};
